import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Divider,
  Stack,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  CalendarTodayOutlined as CalendarIcon,
  PersonOutlined as PersonIcon,
} from "@mui/icons-material";

// ─── Date helpers ─────────────────────────────────────────────────────────────

const toLocalDateKey = (d) => {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const makeFixedDateColumns = (days = 10) => {
  const out   = [];
  const now   = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let i = 0; i <= days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() - i);
    out.push({
      key:      toLocalDateKey(d),
      date:     d,
      label:    d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      sublabel: d.toLocaleDateString(undefined, { weekday: "short" }),
      isToday:  i === 0,
    });
  }
  return out.reverse();
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const stringToColor = (str = "") => {
  let hash = 0;
  for (let i = 0; i < str.length; i++)
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 45%, 52%)`;
};

const initials = (first = "", last = "") =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

// ─── Cell ─────────────────────────────────────────────────────────────────────

function SessionCell({ slot, isToday, programColor }) {
  const theme = useTheme();
  if (!slot) {
    return (
      <TableCell
        align="center"
        sx={{
          px: 1, py: 1,
          bgcolor: isToday ? "action.hover" : "transparent",
          borderRight: "0.5px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 11 }}>
          —
        </Typography>
      </TableCell>
    );
  }

  return (
    <TableCell
      align="center"
      sx={{
        px: 1, py: 0.75,
        bgcolor: isToday ? "action.selected" : "transparent",
        borderRight: "0.5px solid",
        borderColor: "divider",
        verticalAlign: "top",
      }}
    >
      <Stack spacing={0.5} alignItems="center">
        {/* Student count badge */}
        <Box
          sx={{
            px: 1, py: 0.25,
            borderRadius: 1,
            bgcolor:   isToday ? "primary.main" : "action.hover",
            display:  "inline-flex",
            alignItems: "center",
            gap: 0.4,
          }}
        >
          <PersonIcon sx={{ fontSize: 11, color: isToday ? "#fff" : "text.secondary" }} />
          <Typography
            sx={{
              fontSize:   11,
              fontWeight: 700,
              color:      isToday ? "#fff" : "text.primary",
              lineHeight: 1,
            }}
          >
            {slot.total}
          </Typography>
        </Box>

        {/* Program chips */}
        <Stack direction="row" spacing={0.4} flexWrap="wrap" justifyContent="center" useFlexGap>
          {Array.from(slot.byProgram.entries()).map(([prog, count]) => {
            const bg = programColor(prog);
            return (
              <Chip
                key={prog}
                size="small"
                label={`${prog} · ${count}`}
                sx={{
                  height:   18,
                  fontSize: 9,
                  fontWeight: 600,
                  bgcolor:  bg,
                  color:    theme.palette.getContrastText(bg),
                  border:   "none",
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </TableCell>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TutorSessionsByDateTable({ data }) {
  const theme = useTheme();
  const [days, setDays] = useState(10);

  const rows = Array.isArray(data) ? data : [];

  // Normalize
  const normalized = useMemo(() =>
    rows.map((r) => {
      const d = new Date(r.session_date);
      return {
        tutor_id:      r.tutor_id,
        first_name:    r.first_name,
        last_name:     r.last_name,
        dateKey:       toLocalDateKey(d),
        program:       r.program || "Unknown",
        student_count: Number(r.student_count) || 0,
      };
    }),
  [rows]);

  // Date columns
  const dateColumns = useMemo(() => makeFixedDateColumns(days), [days]);

  // Group by tutor
  const tutors = useMemo(() => {
    const m = new Map();
    for (const r of normalized) {
      if (!m.has(r.tutor_id)) {
        m.set(r.tutor_id, {
          tutor_id:   r.tutor_id,
          first_name: r.first_name,
          last_name:  r.last_name,
          byDate:     new Map(),
        });
      }
      const t    = m.get(r.tutor_id);
      if (!t.byDate.has(r.dateKey)) {
        t.byDate.set(r.dateKey, { total: 0, byProgram: new Map() });
      }
      const slot = t.byDate.get(r.dateKey);
      slot.total += r.student_count;
      slot.byProgram.set(r.program, (slot.byProgram.get(r.program) || 0) + r.student_count);
    }
    return Array.from(m.values()).sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    );
  }, [normalized]);

  // Stable program → color map
  const programColor = useMemo(() => {
    const palette = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
    ];
    const map = new Map();
    let i = 0;
    return (name) => {
      if (!map.has(name)) { map.set(name, palette[i++ % palette.length]); }
      return map.get(name);
    };
  }, [theme]);

  // Summary stats
  const totalSessions = useMemo(() =>
    normalized.reduce((s, r) => s + r.student_count, 0),
  [normalized]);

  const activeTutors = tutors.length;

  return (
    <Box sx={{ width: "100%" }}>

      {/* ── Header ──────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography variant="h6" fontWeight={700}>
              Tutor Sessions
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
            <Chip
              label={`${activeTutors} tutor${activeTutors !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 20, borderRadius: 0.5 }}
            />
            <Chip
              label={`${totalSessions} student sessions`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: 10, height: 20, borderRadius: 0.5 }}
            />
          </Stack>
        </Box>

        {/* Date range selector */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            sx={{ fontSize: 13 }}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={10}>Last 10 days</MenuItem>
            <MenuItem value={20}>Last 20 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* ── Table ───────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, overflow: "hidden", width: "100%" }}
      >
        <TableContainer sx={{ maxHeight: "70vh", overflowX: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {/* Sticky tutor column header */}
                <TableCell
                  sx={{
                    minWidth:        180,
                    position:        "sticky",
                    left:            0,
                    zIndex:          3,
                    bgcolor:         "action.hover",
                    borderRight:     "1px solid",
                    borderColor:     "divider",
                    borderBottom:    "1px solid",
                  }}
                >
                  <Typography variant="caption" fontWeight={700}
                    color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Tutor
                  </Typography>
                </TableCell>

                {/* Date columns */}
                {dateColumns.map((dc) => (
                  <TableCell
                    key={dc.key}
                    align="center"
                    sx={{
                      minWidth:     110,
                      bgcolor:      dc.isToday ? "action.selected" : "action.hover",
                      borderRight:  "0.5px solid",
                      borderColor:  "divider",
                      borderBottom: dc.isToday ? "2px solid" : "1px solid",
                      borderBottomColor: dc.isToday ? "primary.main" : "divider",
                      pb:           dc.isToday ? 0.75 : undefined,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={dc.isToday ? 700 : 500}
                      color={dc.isToday ? "primary.main" : "text.primary"}
                      display="block"
                    >
                      {dc.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color={dc.isToday ? "primary.main" : "text.disabled"}
                      sx={{ fontSize: 10 }}
                    >
                      {dc.isToday ? "Today" : dc.sublabel}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {tutors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={1 + dateColumns.length}>
                    <Box sx={{ py: 6, textAlign: "center" }}>
                      <PersonIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        No session data to display.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                tutors.map((t, rowIdx) => {
                  const bg = stringToColor(`${t.first_name} ${t.last_name}`);
                  return (
                    <TableRow
                      key={t.tutor_id}
                      hover
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        bgcolor: rowIdx % 2 === 0 ? "transparent" : "action.focus",
                      }}
                    >
                      {/* Sticky tutor name cell */}
                      <TableCell
                        sx={{
                          position:    "sticky",
                          left:        0,
                          zIndex:      1,
                          bgcolor:     rowIdx % 2 === 0
                            ? "background.paper"
                            : "action.focus",
                          borderRight: "1px solid",
                          borderColor: "divider",
                          minWidth:    180,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: bg }}>
                            {initials(t.first_name, t.last_name)}
                          </Avatar>
                          <Box>
                            <Typography variant="caption" fontWeight={600} display="block">
                              {t.first_name} {t.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
                              ID {t.tutor_id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Session cells */}
                      {dateColumns.map((dc) => (
                        <SessionCell
                          key={dc.key}
                          slot={t.byDate.get(dc.key) ?? null}
                          isToday={dc.isToday}
                          programColor={programColor}
                        />
                      ))}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}